import { ChangeAttrName } from '@common/flare/change-attr-name.enum';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { TrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-change-node-attrs.type';
import { GetTrackedChangeUser, TrackedChangeUser } from '@common/prosemirror/tracked-changes/tracked-change-user.type';
import { TrackedChange } from '@common/prosemirror/tracked-changes/tracked-change.type';
import { ReviewMergeOption } from '@common/reviews/types/review-merge-option.type';
import { Attrs, MarkType, Node } from 'prosemirror-model';
import { v4 as uuidv4 } from 'uuid';

export interface TrackedChangeDataOptions {
  changeAttribute?: string;
  changeContent?: string;
  changeId?: string;
  changeValue?: string;
  changeTimestamp?: ISO8601DateString;
  reviewMergeOption?: ReviewMergeOption;
}

/**
 * Returns the tracked change user by either calling the function or returning the user.
 * @param user The tracked change user or a function that returns the tracked change user.
 * @returns The tracked change user.
 */
export function getTrackedChangeUser(user: TrackedChangeUser | GetTrackedChangeUser): TrackedChangeUser {
  return typeof user === 'function' ? user() : user;
}

/**
 * Creates a tracked change.
 * If no change id is provided, a guid will be used.
 * If no change timestamp is provided, the current date and time will be used.
 * @param changeType The type of change.
 * @param changeUser The user that made the change.
 * @param changeData Optional change data to include.
 * @returns The tracked change.
 */
export function makeTrackedChange(changeType: ChangeType, changeUser: TrackedChangeUser | GetTrackedChangeUser, changeData?: TrackedChangeDataOptions): TrackedChange {
  changeUser = getTrackedChangeUser(changeUser);

  return {
    id: changeData?.changeId ?? uuidv4(),
    userName: changeUser.UserName,
    initials: changeUser.Initials,
    creatorCentralUserId: changeUser.Id,
    timestamp: changeData?.changeTimestamp ?? new Date().toISOString(),
    changeType,
    changeContent: changeData?.changeContent,
    attribute: changeData?.changeAttribute,
    value: changeData?.changeValue,
    reviewMergeOption: changeData?.reviewMergeOption
  };
}

/**
 * Creates tracked change node attrs.
 * If no change id is provided, a guid will be used.
 * If no change timestamp is provided, the current date and time will be used.
 * @param changeType The type of change.
 * @param changeUser The user that made the change.
 * @param changeData Optional change data to include.
 * @returns The tracked change node attrs.
 */
export function makeTrackedChangeNodeAttrs(changeType: ChangeType, changeUser: TrackedChangeUser | GetTrackedChangeUser, changeData?: TrackedChangeDataOptions): TrackedChangeNodeAttrs {
  const change = makeTrackedChange(changeType, changeUser, changeData);
  return {
    [ChangeAttrName.ChangeIds]: change.id,
    changeList: [change]
  };
}

/**
 * Returns the change attrs for the node whether it be changes on a non-text node or mark changes on a text node.
 * @param changeMarkType A change mark type to compare mark types to.
 * @param node The node to get the change attrs from.
 * @returns The node's change attrs.
 */
export function getTrackedChangeNodeAttrs(changeMarkType: MarkType, node: Node): TrackedChangeNodeAttrs {
  if (node.isText || node.type.spec.unchangeable) {
    const mark = node.marks?.find(mark => mark.type === changeMarkType);
    return mark ? toTrackedChangeNodeAttrs(mark.attrs) : undefined;
  } else {
    return toTrackedChangeNodeAttrs(node.attrs);
  }
}

/**
 * Returns a `TrackedChangeNodeAttrs` (a `{ MadCap:changes, changeList }` pair) from an arbitrary node or mark attrs object.
 * For backwards compatibility, if `changeList` is present but the `MadCap:changes` attribute is missing, the `MadCap:changes` value is derived from the change list.
 * @param attrs The node or mark attributes to read tracked change data from.
 * @returns The tracked change attrs, or undefined if the attrs do not represent a tracked change.
 */
export function toTrackedChangeNodeAttrs(attrs: Attrs): TrackedChangeNodeAttrs {
  // If this node has a change id and a change list then it has change attrs
  if (attrs[ChangeAttrName.ChangeIds] && Array.isArray(attrs.changeList)) {
    return { [ChangeAttrName.ChangeIds]: attrs[ChangeAttrName.ChangeIds], changeList: attrs.changeList };
  }
  // Backwards compatibility if the node has changeList but not MadCap:changes
  else if (Array.isArray(attrs.changeList)) {
    return { [ChangeAttrName.ChangeIds]: makeTrackedChangeIdsAttrValueFromChangeList(attrs.changeList), changeList: attrs.changeList };
  }
}

/**
 * Returns the `MadCap:changes` attribute value for a node or mark attrs object.
 * For backwards compatibility, if the `MadCap:changes` attribute is missing but `changeList` is present, the value is derived from the change list.
 * @param attrs The node or mark attributes to read the change ids from.
 * @returns The `MadCap:changes` attribute value, or undefined if the attrs do not represent a tracked change.
 */
export function getTrackedChangeIdsNodeAttr(attrs: Attrs): string {
  if (attrs?.[ChangeAttrName.ChangeIds]) {
    return attrs[ChangeAttrName.ChangeIds];
  } else if (Array.isArray(attrs?.changeList)) {
    return makeTrackedChangeIdsAttrValueFromChangeList(attrs.changeList);
  }
}

/**
 * Converts a MadCap:changes attribute value to an array of change ids.
 * @param changeIdsAttrValue The MadCap:changes attribute value to convert.
 * @returns The change ids.
 */
export function getTrackedChangeIdsFromAttrValue(changeIdsAttrValue: string): string[] {
  return changeIdsAttrValue.length > 0 ? changeIdsAttrValue.split(',') : [];
}

/**
 * Converts an array of change ids to a MadCap:changes attribute value.
 * @param changeIds The change ids to convert.
 * @returns The MadCap:changes attribute value.
 */
export function makeTrackedChangeIdsAttrValue(changeIds: string[]): string {
  return changeIds.join(',');
}

/**
 * Converts an array of tracked changes to a MadCap:changes attribute value.
 * @param changeList The tracked change list to convert.
 * @returns The MadCap:changes attribute value.
 */
export function makeTrackedChangeIdsAttrValueFromChangeList(changeList: TrackedChange[]): string {
  return makeTrackedChangeIdsAttrValue(changeList.map(change => change.id));
}

/**
 * Converts an array of tracked changes to a TrackedChangeNodeAttrs object.
 * @param changeList The tracked change list to convert.
 * @returns The TrackedChangeNodeAttrs object.
 */
export function makeTrackedChangeNodeAttrsFromChangeList(changeList: TrackedChange[]): TrackedChangeNodeAttrs {
  return {
    [ChangeAttrName.ChangeIds]: makeTrackedChangeIdsAttrValueFromChangeList(changeList),
    changeList: changeList
  };
}

/**
 * Removes tracked changes from node attributes.
 * @param attrs The node attributes to remove tracked changes from.
 * @returns A new object of the node attributes without tracked changes.
 */
export function removeTrackedChangesFromNodeAttrs(attrs: Attrs): Attrs {
  if (attrs) {
    const { [ChangeAttrName.ChangeIds]: changeIds, changeList, ...rest } = attrs;
    return rest;
  } else {
    return attrs;
  }
}
